import React from "react";
import { FunctionField, ReferenceField, useRecordContext } from "react-admin";
import { Typography } from "@material-ui/core";

import { getLineupMembers, getOfficials } from "@hisports/scoresheet/src/selectors";
import { isStaffPosition } from "@hisports/scoresheet/src/util";
import { dedupeBy } from "@hisports/parsers";

import { useMeta, useScoresheet } from "../ScoresheetContext";
import { isAuthorized } from "../../../common/Authorize";
import { useSport } from "../../../http";
import { isStaffMember } from "..";

const getPositions = member => {
  if (member?.position) return [member.position];
  if (member?.positions) return member.positions.filter(isStaffPosition);
  return [];
}

const useAllStaffMembers = () => {
  const { game, meta } = useMeta();
  const sport = useSport();

  const officials = useScoresheet(scoresheet => getOfficials(scoresheet, { assignedOnly: true }));
  const homeLineup = useScoresheet(scoresheet => getLineupMembers(scoresheet, { teamId: game?.homeTeamId, sport }));
  const awayLineup = useScoresheet(scoresheet => getLineupMembers(scoresheet, { teamId: game?.awayTeamId, sport }));

  const homeRoster = (meta?.teams[game?.homeTeamId]?.members || []);
  const awayRoster = (meta?.teams[game?.awayTeamId]?.members || []);

  const homeStaff = [...homeLineup, ...homeRoster].filter(member => isStaffMember(member));
  const awayStaff = [...awayLineup, ...awayRoster].filter(member => isStaffMember(member));

  const allStaffMembers = dedupeBy('participantId', [...homeStaff, ...awayStaff, ...officials]);

  return allStaffMembers;
}

export const LineupSignatoryField = ({ includeDetails = true, link = 'show', className, variant = 'body2', ...props }) => {
  const record = useRecordContext(props);
  const allStaffMembers = useAllStaffMembers();

  const showLink = isAuthorized(record, 'participants', 'show') ? link : false;

  const signatory = allStaffMembers.find(member => member.participantId === record?.participantId);
  const signatoryPositions = getPositions(signatory);

  return <ReferenceField basePath="/participants" resource="participants" reference="participants" link={showLink} {...props}>
    <FunctionField render={participant => {
      const details = signatoryPositions.length ? signatoryPositions.join(' / ') : participant?.HCRId;
      return <>
        <Typography variant={variant} display="inline">{participant?.fullName?.toUpperCase()}</Typography>
        {includeDetails && details && (
          <Typography variant={variant} display="inline" color="textSecondary"> ({details})</Typography>
        )}
      </>
    }} variant={variant} className={className} />
  </ReferenceField>
}

LineupSignatoryField.defaultProps = {
  addLabel: true,
}
